import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  FileText,
  Shield,
  AlertTriangle,
  Download,
  Search,
  Eye,
  CheckCircle,
  Clock,
  Brain,
  Globe,
  TrendingUp
} from 'lucide-react';
import { fetchAgents } from '../lib/api';
import { isProduction } from '../lib/production-guard';

interface PatentClaim {
  id: string;
  title: string;
  description: string;
  technicalField: string;
  confidenceScore: number;
  inventors: string[];
  timestamp: string;
  noveltyFactors: string[];
}

interface ReplicationAlert {
  id: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: {
    type: string;
    url: string;
    organization?: string;
  };
  similarity: {
    score: number;
    analysis: string;
  };
  recommendation: {
    action: string;
    timeframe: string;
  };
}

interface ProvisionalApplication {
  id: string;
  title: string;
  inventors: string[];
  filingDate: string;
  claimsCount: number;
  status: string;
  generatedAt: string;
}

export default function PatentProtectionPanel() {
  const [activeTab, setActiveTab] = useState('claims');
  const [claims, setClaims] = useState<PatentClaim[]>([]);
  const [alerts, setAlerts] = useState<ReplicationAlert[]>([]);
  const [applications, setApplications] = useState<ProvisionalApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [, setRegisteredAgents] = useState<any[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load real API data with simulated fallback
  useEffect(() => {
    loadFromAPI();
    pollRef.current = setInterval(loadFromAPI, 15000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadFromAPI = async () => {
    const agentsData = await fetchAgents();
    if (agentsData) {
      const agents = Array.isArray(agentsData) ? agentsData : agentsData.agents || [];
      setRegisteredAgents(agents);

      // Find License Layer agent for IP data
      const licenseAgent = agents.find((a: any) =>
        a.id === 'mod-019' || (a.name && a.name.includes('License'))
      );

      // Build patent claims from real agent names
      if (agents.length > 0) {
        const realClaims: PatentClaim[] = [
          {
            id: 'claim_security',
            title: 'Integrated Security Layer with Immutable Agent Audit Trails',
            description: `System managing ${agents.length} AI agents through tiered security with cryptographic audit logging`,
            technicalField: 'artificial_intelligence_governance',
            confidenceScore: 92,
            inventors: ['Agentomy Development Team', 'Security Architecture Team'],
            timestamp: new Date().toISOString(),
            noveltyFactors: [
              `Orchestration of ${agents.length} agents with persona-based access control`,
              'Combination of tiered security framework with immutable audit trails',
              'Real-time cryptographic verification of agent operations',
            ],
          },
          {
            id: 'claim_persona',
            title: 'Dynamic Multi-Persona Authority Framework for AI Agent Governance',
            description: 'Hierarchical persona management enabling dynamic authority assignment and escalation',
            technicalField: 'multi_agent_systems',
            confidenceScore: 88,
            inventors: ['Agentomy Development Team'],
            timestamp: new Date().toISOString(),
            noveltyFactors: [
              'Dynamic persona tier assignment based on risk assessment',
              'Automated escalation protocols with time-based constraints',
              `Cross-persona consensus across ${agents.length} registered agents`,
            ],
          },
          {
            id: 'claim_trustchain',
            title: 'Cryptographic Hash Chain Verification for AI Agent Audit Trails',
            description: `Tamper-evident audit trails covering ${agents.length} registered agents`,
            technicalField: 'cryptographic_verification',
            confidenceScore: 94,
            inventors: ['Agentomy Cryptography Team'],
            timestamp: new Date().toISOString(),
            noveltyFactors: [
              'Hash chain verification optimized for high-frequency agent operations',
              licenseAgent ? `IP fingerprinting via ${licenseAgent.name || 'License Layer'}` : 'IP fingerprinting via License Layer module',
              'Real-time integrity verification without blockchain overhead',
            ],
          },
        ];
        setClaims(realClaims);
        return; // Real data loaded
      }
    }

    // Fallback to simulated data (dev only)
    if (claims.length === 0 && !isProduction()) {
      initializeMockData();
    }
  };

  const initializeMockData = () => {
    setClaims([
      {
        id: 'claim_1',
        title: 'Integrated Security Layer with Immutable Agent Audit Trails',
        description: 'A system for managing AI agents through tiered security framework combined with cryptographically-secured audit logging',
        technicalField: 'artificial_intelligence_governance',
        confidenceScore: 92,
        inventors: ['Agentomy Development Team', 'Security Architecture Team'],
        timestamp: '2025-01-26T10:00:00Z',
        noveltyFactors: [
          'Integration of persona-based tiered access control with AI agent management',
          'Combination of tiered security framework with immutable audit trails',
          'Real-time cryptographic verification of agent operations'
        ]
      },
      {
        id: 'claim_2', 
        title: 'Dynamic Multi-Persona Authority Framework for AI Agent Governance',
        description: 'A hierarchical persona management system enabling dynamic assignment and escalation of authority levels',
        technicalField: 'multi_agent_systems',
        confidenceScore: 88,
        inventors: ['Agentomy Development Team'],
        timestamp: '2025-01-26T10:15:00Z',
        noveltyFactors: [
          'Dynamic persona tier assignment based on risk assessment',
          'Automated escalation protocols with time-based constraints',
          'Cross-persona consensus requirements for high-risk operations'
        ]
      },
      {
        id: 'claim_3',
        title: 'Cryptographic Hash Chain Verification for AI Agent Audit Trails',
        description: 'A system employing cryptographic hash chaining to create verifiable, tamper-evident audit trails',
        technicalField: 'cryptographic_verification',
        confidenceScore: 94,
        inventors: ['Agentomy Cryptography Team'],
        timestamp: '2025-01-26T10:30:00Z',
        noveltyFactors: [
          'Hash chain verification optimized for high-frequency agent operations',
          'Integration of cryptographic proofs with operational audit trails',
          'Real-time integrity verification without blockchain overhead'
        ]
      }
    ]);

    setAlerts([
      {
        id: 'alert_1',
        severity: 'high',
        source: {
          type: 'github',
          url: 'https://github.com/enterprise-ai/ai-governance-framework',
          organization: 'enterprise-ai'
        },
        similarity: {
          score: 78,
          analysis: 'Repository shows 78% conceptual similarity to patent claim. Key overlaps in governance and security concepts.'
        },
        recommendation: {
          action: 'legal_review',
          timeframe: '7 days'
        }
      },
      {
        id: 'alert_2',
        severity: 'medium',
        source: {
          type: 'patent_database',
          url: 'https://patents.uspto.gov/search'
        },
        similarity: {
          score: 45,
          analysis: 'Prior art shows conceptual overlap but differs in implementation and scope'
        },
        recommendation: {
          action: 'monitor',
          timeframe: '90 days'
        }
      }
    ]);

    setApplications([
      {
        id: 'prov_1',
        title: 'Integrated Security Layer with Immutable Agent Audit Trails',
        inventors: ['Agentomy Development Team'],
        filingDate: '2025-01-26',
        claimsCount: 15,
        status: 'draft',
        generatedAt: '2025-01-26T10:45:00Z'
      }
    ]);
  };

  const handleScanModules = async () => {
    setLoading(true);
    try {
      const agentsData = await fetchAgents();
      if (agentsData) {
        const agents = Array.isArray(agentsData) ? agentsData : agentsData.agents || [];
        setRegisteredAgents(agents);

        // Generate fingerprint-style claims from agent registry
        if (agents.length > 0) {
          const agentNames = agents.slice(0, 5).map((a: any) => a.name || a.id).join(', ');
          const scanAlert: ReplicationAlert = {
            id: `alert_scan_${Date.now()}`,
            severity: 'low',
            source: { type: 'agent_registry', url: '#' },
            similarity: { score: 12, analysis: `Scanned ${agents.length} modules (${agentNames}...). No significant external replication detected.` },
            recommendation: { action: 'monitor', timeframe: '90 days' },
          };
          setAlerts(prev => [scanAlert, ...prev]);
        }
      }
    } catch {
      // API unavailable
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateProvisional = async (_claimId: string) => {
    setLoading(true);
    try {
      // Mock provisional generation
      setTimeout(() => {
        const newApp: ProvisionalApplication = {
          id: `prov_${Date.now()}`,
          title: 'AI Agent Governance System Patent Application',
          inventors: ['Agentomy Development Team'],
          filingDate: new Date().toISOString().slice(0, 10),
          claimsCount: 12,
          status: 'draft',
          generatedAt: new Date().toISOString()
        };
        setApplications([...applications, newApp]);
        setLoading(false);
      }, 1500);
    } catch (error) {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-ag-critical bg-red-900 border-red-600';
      case 'high': return 'text-orange-400 bg-orange-900 border-orange-600';
      case 'medium': return 'text-ag-warning bg-yellow-900 border-yellow-600';
      case 'low': return 'text-ag-active bg-green-900 border-green-600';
      default: return 'text-[#A8A29E] bg-gray-900 border-[#4A443D]';
    }
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 90) return 'text-ag-active';
    if (score >= 80) return 'text-ag-warning';
    if (score >= 70) return 'text-orange-400';
    return 'text-ag-critical';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'filed': return 'text-ag-active bg-green-900 border-green-600';
      case 'pending': return 'text-blue-400 bg-blue-900 border-blue-600';
      case 'approved': return 'text-ag-active bg-green-900 border-green-600';
      case 'rejected': return 'text-ag-critical bg-red-900 border-red-600';
      default: return 'text-[#A8A29E] bg-gray-900 border-[#4A443D]';
    }
  };

  const filteredClaims = claims.filter(claim =>
    claim.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    claim.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center">
            📜 Patent Protection Protocol
          </h2>
          <p className="text-[#A8A29E]">Secure Agentomy's core innovations with automated IP protection</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button 
            onClick={handleScanModules}
            disabled={loading}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            {loading ? (
              <Clock className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Brain className="w-4 h-4 mr-2" />
            )}
            Scan Modules
          </Button>
          <Badge variant="outline" className="text-ag-active border-ag-active">
            IP Protected
          </Badge>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Patent Claims</p>
                <p className="text-2xl font-bold text-cyan-400">{claims.length}</p>
              </div>
              <FileText className="w-8 h-8 text-cyan-400 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Replication Alerts</p>
                <p className="text-2xl font-bold text-ag-warning">{alerts.length}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-ag-warning opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Applications</p>
                <p className="text-2xl font-bold text-green-400">{applications.length}</p>
              </div>
              <Shield className="w-8 h-8 text-green-400 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Avg Confidence</p>
                <p className="text-2xl font-bold text-purple-400">
                  {claims.length > 0 ? Math.round(claims.reduce((sum, c) => sum + c.confidenceScore, 0) / claims.length) : 0}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-400 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-[#3A352F]">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'claims', label: 'Patent Claims', icon: FileText },
            { id: 'alerts', label: 'Replication Alerts', icon: AlertTriangle },
            { id: 'applications', label: 'Applications', icon: Shield },
            { id: 'landscape', label: 'IP Landscape', icon: Globe }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-cyan-500 text-cyan-400'
                  : 'border-transparent text-[#A8A29E] hover:text-[#A8A29E] hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Sections */}
      {activeTab === 'claims' && (
        <div className="space-y-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
            <Input
              placeholder="Search patent claims..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#292524] border-[#4A443D] text-white"
            />
          </div>

          {/* Claims List */}
          <div className="space-y-4">
            {filteredClaims.map(claim => (
              <Card key={claim.id} className="bg-gray-900 border-[#3A352F]">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{claim.title}</h3>
                        <Badge className={`${getConfidenceColor(claim.confidenceScore)}`}>
                          {claim.confidenceScore}% Confidence
                        </Badge>
                      </div>
                      <p className="text-[#A8A29E] text-sm mb-3">{claim.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-[#A8A29E]">Technical Field:</span>
                          <div className="text-white capitalize">{claim.technicalField.replace(/_/g, ' ')}</div>
                        </div>
                        <div>
                          <span className="text-[#A8A29E]">Inventors:</span>
                          <div className="text-white">{claim.inventors.join(', ')}</div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <span className="text-[#A8A29E] text-sm">Key Novelty Factors:</span>
                        <ul className="mt-2 space-y-1">
                          {claim.noveltyFactors.slice(0, 2).map((factor, index) => (
                            <li key={index} className="text-sm text-[#A8A29E] flex items-start">
                              <CheckCircle className="w-3 h-3 text-green-400 mr-2 mt-1 flex-shrink-0" />
                              {factor}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="flex space-x-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleGenerateProvisional(claim.id)}
                        disabled={loading}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        📝 Generate Filing
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {alerts.map(alert => (
            <Card key={alert.id} className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <Badge className={getSeverityColor(alert.severity)}>
                        {alert.severity.toUpperCase()}
                      </Badge>
                      <span className="text-white font-medium">{alert.source.type.toUpperCase()}</span>
                      {alert.source.organization && (
                        <span className="text-[#A8A29E]">• {alert.source.organization}</span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-[#A8A29E]">Similarity Score:</span>
                        <div className="text-yellow-400 font-bold">{alert.similarity.score}%</div>
                      </div>
                      <div>
                        <span className="text-[#A8A29E]">Recommended Action:</span>
                        <div className="text-white capitalize">{alert.recommendation.action.replace(/_/g, ' ')}</div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <span className="text-[#A8A29E] text-sm">Analysis:</span>
                      <p className="text-[#A8A29E] text-sm mt-1">{alert.similarity.analysis}</p>
                    </div>
                  </div>

                  <div className="flex space-x-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]"
                    >
                      View Source
                    </Button>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Take Action
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'applications' && (
        <div className="space-y-4">
          {applications.map(app => (
            <Card key={app.id} className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-lg font-semibold text-white">{app.title}</h3>
                      <Badge className={getStatusColor(app.status)}>
                        {app.status.toUpperCase()}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-[#A8A29E]">Filing Date:</span>
                        <div className="text-white">{app.filingDate}</div>
                      </div>
                      <div>
                        <span className="text-[#A8A29E]">Claims Count:</span>
                        <div className="text-white">{app.claimsCount}</div>
                      </div>
                      <div>
                        <span className="text-[#A8A29E]">Inventors:</span>
                        <div className="text-white">{app.inventors.join(', ')}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      USPTO
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      WIPO
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'landscape' && (
        <div className="space-y-6">
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="text-white">Patent Landscape Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-[#292524] rounded-lg">
                  <div className="text-3xl font-bold text-green-400 mb-2">3</div>
                  <div className="text-sm text-[#A8A29E]">High-Confidence Claims</div>
                  <div className="text-xs text-gray-500">Recommended for filing</div>
                </div>
                <div className="text-center p-4 bg-[#292524] rounded-lg">
                  <div className="text-3xl font-bold text-yellow-400 mb-2">$45K</div>
                  <div className="text-sm text-[#A8A29E]">Estimated Filing Costs</div>
                  <div className="text-xs text-gray-500">Provisional + utility</div>
                </div>
                <div className="text-center p-4 bg-[#292524] rounded-lg">
                  <div className="text-3xl font-bold text-purple-400 mb-2">30</div>
                  <div className="text-sm text-[#A8A29E]">Days to File</div>
                  <div className="text-xs text-gray-500">Recommended timeline</div>
                </div>
              </div>

              <div className="p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
                <h4 className="text-blue-300 font-semibold mb-3">📋 Filing Strategy Recommendations</h4>
                <ul className="space-y-2 text-sm text-blue-200">
                  <li>• File provisional applications for Security Layer + Audit Trail claims (high priority)</li>
                  <li>• Conduct freedom-to-operate analysis for multi-persona framework</li>
                  <li>• Consider PCT filing for core innovations targeting international markets</li>
                  <li>• Monitor competitor activity in AI governance space</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}